import { useState } from 'react';
import { FieldErrors, UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';

import { getTemplates } from '@/api/getTemplates';
import Button from '@/components/Button/Button';
import MultiFileUpload from '@/components/MultiFileUpload/MultiFileUpload';
import { SelectTemplateDialog } from '@/components/templates/SelectTemplateDialog';
import TextAreaInput from '@/components/TextArea/TextAreaInput';
import { SupportRecordFormData } from '@/pages/admin/reservations/support-records/[id]';
import { SupportRecord } from '@/types/supportRecord';

type Props = {
  supportRecord: SupportRecord | undefined;
  register: UseFormRegister<SupportRecordFormData>;
  watch: UseFormWatch<SupportRecordFormData>;
  setValue: UseFormSetValue<SupportRecordFormData>;
  errors: FieldErrors<SupportRecordFormData>;
  setSupportRecordImageFiles: (v: File[]) => void;
};

type TemplateTargetField = keyof Pick<SupportRecordFormData, 'supportContent' | 'staffMessage'>;

export const SupportAndTreatmentForm = ({
  supportRecord,
  register,
  setValue,
  errors,
  setSupportRecordImageFiles,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateCategory, setTemplateCategory] = useState<string | null>(null);
  const [targetField, setTargetField] = useState<TemplateTargetField | null>(null);
  const [templateOptions, setTemplateOptions] = useState<{ id: number; body: string }[]>([]);
  const [loadingCategory, setLoadingCategory] = useState<string | null>(null);

  const handleOpenTemplateDialog = async (category: string, field: TemplateTargetField) => {
    setLoadingCategory(category);
    try {
      const { status, result } = await getTemplates();
      if (status === 200 && result.length > 0) {
        const filtered = result.filter((t) => t.category === category);
        if (filtered.length === 0) {
          alert(`「${category}」カテゴリのテンプレートが見つかりませんでした`);
          return;
        }
        setTemplateOptions(filtered);
        setTemplateCategory(category);
        setTargetField(field);
        setIsModalOpen(true);
      } else {
        alert('テンプレートが見つかりませんでした');
      }
    } catch (err) {
      console.error('テンプレート取得失敗:', err);
    } finally {
      setLoadingCategory(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="w-full space-y-2">
        <TextAreaInput
          label="支援・療育記録"
          errors={errors.supportContent}
          {...register('supportContent')}
        />
        <Button
          size="xs"
          variant="outline"
          onClick={() => handleOpenTemplateDialog('支援記録', 'supportContent')}
          disabled={loadingCategory === '支援記録'}
        >
          {loadingCategory === '支援記録' ? '読み込み中...' : '定型文から入力'}
        </Button>
      </div>

      <div className="w-full space-y-2">
        <TextAreaInput
          label="連絡事項（保護者向け）"
          errors={errors.staffMessage}
          {...register('staffMessage')}
        />
        <Button
          size="xs"
          variant="outline"
          onClick={() => handleOpenTemplateDialog('連絡帳', 'staffMessage')}
          disabled={loadingCategory === '連絡帳'}
        >
          {loadingCategory === '連絡帳' ? '読み込み中...' : '定型文から入力'}
        </Button>
      </div>

      <div className="w-full">
        <TextAreaInput
          label="業務日誌（スタッフ向け）"
          errors={errors.staffReport}
          {...register('staffReport')}
        />
      </div>

      <div className="w-full">
        <MultiFileUpload
          label="写真アップロード"
          buttonText="画像アップロード"
          canPreview
          accept="image/*"
          setFiles={setSupportRecordImageFiles}
          initialValues={supportRecord?.support_record_images}
        />
      </div>

      {/* SelectTemplateDialog モーダル */}
      {isModalOpen && templateCategory && targetField && (
        <SelectTemplateDialog
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          templates={templateOptions}
          onSubmit={(selectedTemplate) => {
            setValue(targetField, selectedTemplate.body);
          }}
          label={templateCategory ?? '定型文'}
        />
      )}
    </div>
  );
};
